import React, { useEffect } from 'react';
import { DatePicker, Form, Radio, Table, Button, Input, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { CheckCircleOutlined } from '@ant-design/icons';
import numeral from 'numeral';
import moment from 'moment';
import useQuotesContext from './QuotesContext';
import { fractionType } from '../../utils/constants/misc';
import {
  FamilyIcon,
  SingleParentIcon,
  SoloIcon,
  HouseIcon,
  OfficeIcon
} from '../../utils/constants/formIcons';
import { genders } from '../../utils/constants/enums';

const usePrevoyanceFields = (form) => {
  const { t } = useTranslation();
  const {
    estimatedCost,
    exemptions,
    formValues,
    setFormValues
  } = useQuotesContext();

  const dateFormat = 'DD/MM/YYYY';
  const eihteenYearsAgo = moment().subtract(18, 'years');
  const twentySevenYearsAgo = moment().subtract(27, 'years');
  const seventyYearsAgo = moment().subtract(70, 'years');
  const { Option } = Select;
  const professionList = [
    'salary',
    'official',
    'TNS',
    'farmer',
    'without_profession'
  ];
  const isAmendment = window.location.href.search('amendment');

  const costGivenFractionType = (fraction) =>
    `${t(`contracts.form.options.${fraction}`)} ${
      !exemptions.length
        ? `${(
            estimatedCost.total_monthly_customer * fractionType[fraction]
          ).toFixed(2)}€/${t(`quotes.fraction_type.${fraction}`)}`
        : ''
    }`;

  useEffect(() => {
    form.setFieldsValue({ capital: 10000 });
    setFormValues({
      ...formValues,
      capital: 10000
    });
    // eslint-disable-next-line
  }, [formValues?.period]);

  const prevoyanceFields = [
    {
      name: ['customer_type'],
      rules: [{ required: true }],
      input: (
        <Radio.Group buttonStyle="solid">
          <Radio.Button value="INDIVIDUAL">
            {t('contracts.form.options.INDIVIDUAL')}
          </Radio.Button>
          <Radio.Button value="COMPANY">
            {t('contracts.form.options.COMPANY')}
          </Radio.Button>
        </Radio.Group>
      ),
      step: 0
    },
    {
      name: ['package'],
      input: (
        <Radio.Group buttonStyle="solid" className="radio-with-icon">
          <Radio.Button value="SOLO">
            <SoloIcon />
            {t(`contracts.form.options.SOLO`)}
          </Radio.Button>
          <Radio.Button value="MONO">
            <SingleParentIcon />
            {t(`contracts.form.options.MONO`)}
          </Radio.Button>
          <Radio.Button value="FAMILY">
            <FamilyIcon />
            {t(`contracts.form.options.FAMILY`)}
          </Radio.Button>
        </Radio.Group>
      ),
      tooltip: true,
      step: 0
    },
    {
      name: ['adult_1'],
      input: (
        <>
          <Form.Item
            key={['adult_1', 'civility']}
            name={['adult_1', 'civility']}
            label={t('contracts.form.children_v.civility')}
            rules={[{ required: true }]}
          >
            <Select
              onChange={(value) =>
                form.setFieldsValue({
                  customer: {
                    gender: value
                  }
                })
              }
            >
              {genders.map((g) => (
                <Option value={g} key={g}>
                  {t(`insurers.form.${g}`)}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            key={['adult_1', 'name']}
            name={['adult_1', 'name']}
            label={t('contracts.form.children_v.name')}
            rules={[{ required: true }]}
            onChange={(e) =>
              form.setFieldsValue({
                customer: {
                  last_name: e.target.value
                }
              })
            }
          >
            <Input />
          </Form.Item>
          <Form.Item
            key={['adult_1', 'firstName']}
            name={['adult_1', 'firstName']}
            label={t('contracts.form.children_v.firstName')}
            rules={[{ required: true }]}
            onChange={(e) =>
              form.setFieldsValue({
                customer: {
                  first_name: e.target.value
                }
              })
            }
          >
            <Input />
          </Form.Item>
          <Form.Item
            key={['adult_1', 'profession']}
            name={['adult_1', 'profession']}
            label={t('contracts.form.profession')}
            rules={[{ required: true }]}
            tooltip={t('contracts.form.tooltip.profession')}
          >
            <Select>
              {professionList.map((e) => (
                <Option value={e} key={e}>
                  {t(`contracts.form.options.${e}`)}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            key={['adult_1', 'dob']}
            name={['adult_1', 'dob']}
            label={t('contracts.form.children_v.dob')}
            rules={[{ required: true }]}
          >
            <DatePicker
              format={dateFormat}
              picker="date"
              showToday={false}
              defaultPickerValue={moment('1990-01-01')}
              style={{ width: 200 }}
              inputReadOnly={false}
              disabledDate={(current) => {
                const before = current < seventyYearsAgo;
                const after = current >= eihteenYearsAgo;
                return before || after;
              }}
            />
          </Form.Item>
        </>
      ),
      step: 1
    },
    ...(formValues && formValues.package === 'FAMILY'
      ? [
          {
            name: ['adult_2'],
            input: (
              <>
                <Form.Item
                  key={['adult_2', 'civility']}
                  name={['adult_2', 'civility']}
                  label={t('contracts.form.children_v.civility')}
                  rules={[{ required: true }]}
                >
                  <Select>
                    {genders.map((g) => (
                      <Option value={g} key={g}>
                        {t(`insurers.form.${g}`)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  key={['adult_2', 'name']}
                  name={['adult_2', 'name']}
                  label={t('contracts.form.children_v.name')}
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  key={['adult_2', 'firstName']}
                  name={['adult_2', 'firstName']}
                  label={t('contracts.form.children_v.firstName')}
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  key={['adult_2', 'profession']}
                  name={['adult_2', 'profession']}
                  label={t('contracts.form.profession')}
                  rules={[{ required: true }]}
                  tooltip={t('contracts.form.tooltip.profession')}
                >
                  <Select>
                    {professionList.map((e) => (
                      <Option value={e} key={e}>
                        {t(`contracts.form.options.${e}`)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  key={['adult_2', 'dob']}
                  name={['adult_2', 'dob']}
                  label={t('contracts.form.children_v.dob')}
                  rules={[{ required: true }]}
                >
                  <DatePicker
                    format={dateFormat}
                    picker="date"
                    showToday={false}
                    defaultPickerValue={moment('1990-01-01')}
                    style={{ width: 200 }}
                    inputReadOnly={false}
                    disabledDate={(current) => {
                      const before = current < seventyYearsAgo;
                      const after = current >= eihteenYearsAgo;
                      return before || after;
                    }}
                  />
                </Form.Item>
              </>
            ),
            step: 1
          }
        ]
      : []),
    ...(formValues && formValues?.package !== 'SOLO'
      ? [
          {
            name: ['children'],
            noStyle: true,
            input: (
              <Form.List name="children" initialValue={[{}]}>
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field) => (
                      <div key={field.name}>
                        <Form.Item
                          {...field}
                          key={[field.name, 'civility']}
                          name={[field.name, 'civility']}
                          label={t('contracts.form.children_v.civility')}
                          rules={[{ required: true }]}
                        >
                          <Select>
                            {genders.map((g) => (
                              <Option value={g} key={g}>
                                {t(`insurers.form.${g}`)}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                        <Form.Item
                          {...field}
                          key={[field.name, 'name']}
                          name={[field.name, 'name']}
                          label={t('contracts.form.children_v.name')}
                          rules={[{ required: true }]}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          {...field}
                          key={[field.name, 'firstName']}
                          name={[field.name, 'firstName']}
                          label={t('contracts.form.children_v.firstName')}
                          rules={[{ required: true }]}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          {...field}
                          key={[field.name, 'dob']}
                          name={[field.name, 'dob']}
                          label={t('contracts.form.children_v.dob')}
                          rules={[{ required: true }]}
                        >
                          <DatePicker
                            format={dateFormat}
                            picker="date"
                            showToday={false}
                            defaultPickerValue={moment('1990-01-01')}
                            style={{ width: 200 }}
                            inputReadOnly={false}
                            disabledDate={(current) => {
                              const before = current < seventyYearsAgo;
                              const after = current >= eihteenYearsAgo;
                              return before || after;
                            }}
                          />
                        </Form.Item>
                        {formValues?.children?.length &&
                          formValues?.children?.length > 1 && (
                            <Form.Item>
                              <Button
                                type="link"
                                danger
                                onClick={() => remove(field.name)}
                              >
                                {t('buttons.removeChildren')}
                              </Button>
                            </Form.Item>
                          )}
                      </div>
                    ))}
                    {formValues && formValues.children?.length === 5 ? (
                      []
                    ) : (
                      <Form.Item>
                        <Button type="primary" onClick={() => add()}>
                          {t('buttons.addAnotherChildren')}
                        </Button>
                      </Form.Item>
                    )}
                  </>
                )}
              </Form.List>
            ),
            step: 1
          }
        ]
      : []),
    {
      name: ['period'],
      input: (
        <Radio.Group
          buttonStyle="solid"
          className="radio-with-icon radio-with-icon-text-wrap"
        >
          <Radio.Button value="PRIVATE">
            <HouseIcon />
            {t('contracts.form.options.PRIVATE')}
          </Radio.Button>
          <Radio.Button value="PRIVATE_PRO">
            <OfficeIcon />
            {t('contracts.form.options.PRIVATE_PRO')}
          </Radio.Button>
        </Radio.Group>
      ),
      tooltip: true,
      step: 0
    },
    {
      name: ['capital'],
      input: (
        <Radio.Group buttonStyle="solid">
          {[10000, 25000, 50000, 75000, 100000, 125000, 150000]
            .filter((n, k) => {
              if (formValues && formValues.period === 'PRIVATE_PRO') {
                return k <= 2;
              }
              return n;
            })
            .map((n) => (
              <Radio.Button key={n} value={n}>
                {`${numeral(n).format('0,0')}€`}
              </Radio.Button>
            ))}
        </Radio.Group>
      ),
      tooltip: true,
      step: 0
    },
    {
      name: ['guarantee'],
      noStyle: true,
      noLabel: true,
      input: (
        <Table
          id="prevoyance-table"
          rowKey="label"
          pagination={false}
          dataSource={[
            { label: t('contracts.form.death') },
            { label: t('contracts.form.disability') }
          ]}
          columns={[
            { title: 'Prévoyance accident', dataIndex: 'label' },
            { key: 'icon', render: () => <CheckCircleOutlined /> }
          ]}
        />
      ),
      step: 0
    },
    {
      name: ['recipient_clause'],
      step: 0,
      input: (
        <Radio.Group buttonStyle="solid">
          <Radio.Button value="STANDARD">
            {t('contracts.form.options.STANDARD')}
          </Radio.Button>
          <Radio.Button value="CUSTOM">
            {t('contracts.form.options.CUSTOM')}
          </Radio.Button>
        </Radio.Group>
      ),
      tooltip: true,
      help:
        form.getFieldValue('recipient_clause') === 'STANDARD'
          ? "Les capitaux seront versés à mon conjoint à la date du décès, à défaut à mes enfants vivants ou en cas de décès de l'un d'entre eux à ses représentants, à défaut à mes héritiers."
          : "Une pièce d'identité du bénéficiaire (CNI, passeport ou permis de conduire) sera nécessaire"
    },
    ...(form.getFieldValue('recipient_clause') === 'CUSTOM'
      ? [
          {
            name: ['recipient', 'first_name'],
            rules: [{ required: true }],
            step: 0
          },
          {
            name: ['recipient', 'last_name'],
            rules: [{ required: true }],
            step: 0
          },
          {
            name: ['recipient', 'dob'],
            input: (
              <DatePicker
                format={dateFormat}
                picker="date"
                showToday={false}
                defaultPickerValue={moment('1990-01-01')}
                style={{ width: 200 }}
                inputReadOnly={false}
                disabledDate={(current) => {
                  const before = current < seventyYearsAgo;
                  const after = current >= eihteenYearsAgo;
                  return before || after;
                }}
              />
            ),
            rules: [{ required: true }],
            step: 0
          }
        ]
      : []),
    {
      name: ['fraction_type'],
      rules: [{ required: true }],
      tooltip: true,
      input: (
        <Radio.Group buttonStyle="solid" disabled={isAmendment !== -1}>
          <Radio.Button value="MONTHLY">
            {costGivenFractionType('MONTHLY')}
            {t('contracts.form.options.MONTHLY')}
          </Radio.Button>
          <Radio.Button value="YEARLY">
            {t('contracts.form.options.YEARLY')}
          </Radio.Button>
        </Radio.Group>
      ),
      step: 0
    }
  ];

  return { prevoyanceFields };
};

export default usePrevoyanceFields;
